import React, { useEffect, useState } from 'react';
import { alpha, Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import AppGridContainer from '@crema/components/AppGridContainer';
import Grid from '@mui/material/Grid';
import IntlMessages from '@crema/helpers/IntlMessages';
import { useDropzone } from 'react-dropzone';
import { Form } from 'formik';
import PropTypes from 'prop-types';
import AppTextField from '@crema/components/AppFormComponents/AppTextField';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { Fonts } from '@crema/constants/AppEnums';
import { getDataApi, uploadPostDataApi, uploadPutDataApi, useGetDataApi } from '@crema/hooks/APIHooks';
import { useInfoViewActionsContext } from '@crema/context/AppContextProvider/InfoViewContextProvider';

const AvatarViewWrapper = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    cursor: 'pointer',
    '& .edit-icon': {
      position: 'absolute',
      bottom: 0,
      right: 0,
      zIndex: 1,
      border: `solid 2px ${theme.palette.background.paper}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.7),
      color: theme.palette.primary.contrastText,
      borderRadius: '50%',
      width: 26,
      height: 26,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      '& .MuiSvgIcon-root': {
        fontSize: 16,
      },
    },
    '&.dropzone': {
      outline: 0,
      '&:hover .edit-icon, &:focus .edit-icon': {
        display: 'flex',
      },
    },
  };
});

const PersonalInfoForm = ({ values, setFieldValue }) => {
  const [{ apiData: cities }] = useGetDataApi('/address/v2/cities');
  const [wards, setWards] = useState();
  const infoViewActionsContext = useInfoViewActionsContext();
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: async (acceptedFiles) => {
      const data =
        await uploadPostDataApi('/media', infoViewActionsContext, { file: acceptedFiles[0] });
      if (data.id) {
        setFieldValue('avatar_id', data.id);
        setFieldValue('photoURL', data.source_url);
      }
    },
  });

  const getWards = async (city) => {
    const resp = await getDataApi(`/address/v2/city-wards/${city}`, infoViewActionsContext, {});
    setWards(resp.data);
  }

  const onCityChange = async (value) => {
    setFieldValue('city', value);
    await getWards(value);
  }

  useEffect(() => {
    if (values.city) {
      getWards(values.city)
    }
  }, []);

  return (
    <Form noValidate autoComplete='off'>
      <Typography
        component='h3'
        sx={{
          fontSize: 16,
          fontWeight: Fonts.BOLD,
          mb: { xs: 3, lg: 4 },
        }}
      >
        <IntlMessages id='common.personalInfo' />
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 5, lg: 6 },
        }}
      >
        <AvatarViewWrapper {...getRootProps({ className: 'dropzone' })}>
          <input {...getInputProps()} />
          <label htmlFor='icon-button-file'>
            <Avatar
              sx={{
                width: { xs: 50, lg: 64 },
                height: { xs: 50, lg: 64 },
                cursor: 'pointer',
              }}
              src={values.photoURL}
            />
            <Box className='edit-icon'>
              <EditIcon />
            </Box>
          </label>
        </AvatarViewWrapper>
        <Box
          sx={{
            ml: 4,
          }}
        >
          <Typography
            sx={{
              fontWeight: Fonts.MEDIUM,
            }}
          >
            {values.displayName}
          </Typography>
          <Typography
            sx={{
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            {values.email}
          </Typography>
        </Box>
      </Box>
      <AppGridContainer spacing={4}>
        <Grid item xs={12} md={6}>
          <AppTextField
            name='displayName'
            fullWidth
            label={<IntlMessages id='common.fullName' />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AppTextField
            fullWidth
            name='phone'
            label={<IntlMessages id='common.phoneNumber' />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AppTextField
            name='email'
            fullWidth
            label={<IntlMessages id='common.email' />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AppTextField
            name='company'
            fullWidth
            label={<IntlMessages id='common.company' />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <FormControl
            sx={{
              width: '100%',
            }}
            variant='outlined'
          >
            <InputLabel
              id='demo-simple-select-outlined-label'
            >
              City
            </InputLabel>
            <Select label='city' name='city' onChange={(e) => onCityChange(e.target.value)} value={values.city}>
              {cities && cities.data && Object.keys(cities.data).map(key => {
                return (
                  <MenuItem
                    value={key}
                    key={key}
                    sx={{
                      cursor: 'pointer',
                      inputVariant: 'outlined',
                    }}
                  >
                    {cities.data[key]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <FormControl
            sx={{
              width: '100%',
            }}
            variant='outlined'
          >
            <InputLabel
              id='demo-simple-select-outlined-label'
            >
              Ward
            </InputLabel>
            <Select
              label='ward'
              name='ward'
              placeholder='Select ward'
              onChange={(event) =>
                setFieldValue('ward', event.target.value)
              }
              value={values.ward}
            >
              {wards && Object.keys(wards).map(key => {
                return (
                  <MenuItem
                    value={key}
                    key={key}
                    sx={{
                      cursor: 'pointer',
                      inputVariant: 'outlined',
                    }}
                  >
                    {wards[key]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12}>
          <AppTextField
            sx={{
              width: '100%',
              fontWeight: Fonts.LIGHT
            }}
            variant='outlined'
            label={<IntlMessages id='common.address' />}
            name='address'
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <AppTextField
            sx={{
              width: '100%',
              fontWeight: Fonts.LIGHT,
            }}
            variant='outlined'
            label={<IntlMessages id='seller.certificate' />}
            name='certificate'
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <AppTextField
            multiline
            name='description'
            rows={3}
            fullWidth
            label={<IntlMessages id='common.yourBioDataHere' />}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Button
              sx={{
                position: 'relative',
                minWidth: 100,
              }}
              color='primary'
              variant='contained'
              type='submit'
            >
              <IntlMessages id='common.saveChanges' />
            </Button>
          </Box>
        </Grid>
      </AppGridContainer>
    </Form>
  );
};

export default PersonalInfoForm;
PersonalInfoForm.propTypes = {
  setFieldValue: PropTypes.func,
  values: PropTypes.object,
};
